import { toast } from "react-toastify";
import { handleError } from "../../../../utils/helpers";
import {
  AdminOverviewQueryParams,
  BaseApiResponse,
  FinancialSummaryParams,
  QueryParams,
} from "../../../../utils/types";
import { adminBaseApi } from "../../../api";
import {
  GetEventGuestResponse,
  GetEventGuestsResponse,
  GetEventTicketsBreakdownResponse,
  GetEventTicketsResponse,
} from "../../event/event.types";
import {
  AdminDashboardStats,
  GetAllEventsQuery,
  GetAllEventsResponse,
  GetAllUserEventsQuery,
  GetAllUserEventsResponse,
  MonthlyEventGuestList,
  MonthlyEventsSummary,
  RecentEventsResponse,
  UpdateEventFlag,
  UpdateEventTrending,
} from "./events.types";

export const adminEventsApi = adminBaseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getAllEvents: builder.query<GetAllEventsResponse, GetAllEventsQuery>({
      query: (params) => {
        return {
          url: `admin/events`,
          method: "GET",
          params: { ...params },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getAllUserEvents: builder.query<
      GetAllUserEventsResponse,
      {
        userId: string;
        params: GetAllUserEventsQuery;
      }
    >({
      query: (params) => {
        return {
          url: `admin/users/${params.userId}/events`,
          method: "GET",
          params: { ...params.params },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    adminGetEventTickets: builder.query<
      GetEventTicketsResponse,
      QueryParams & { id?: string; slug?: string }
    >({
      query: ({ id, slug, ...params }) => {
        return {
          url: `events/${slug ? "slug/" : ""}${id ?? encodeURIComponent(slug ?? "")}/tickets`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    adminGetEventTicketsBreakdown: builder.query<
      GetEventTicketsBreakdownResponse,
      QueryParams & { id: string }
    >({
      query: ({ id, ...params }) => {
        return {
          url: `events/${id}/ticket-breakdown`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    adminGetEventGuests: builder.query<
      GetEventGuestsResponse,
      QueryParams & { id: string; search: string; status?: string }
    >({
      query: ({ id, ...params }) => {
        return {
          url: `admin/events/${id}/guests`,
          method: "GET",
          params: {
            ...params,
            search: params.search === "" ? undefined : params.search,
            status: params.status,
          },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    adminGetEventGuest: builder.query<GetEventGuestResponse, { id: string }>({
      query: (params) => {
        return {
          url: `guests/${params.id}/details`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    adminUpdateEventTrending: builder.mutation<
      BaseApiResponse,
      UpdateEventTrending
    >({
      query: ({ id, ...payload }) => {
        return {
          url: `admin/events/${id}/trending-status`,
          method: "PATCH",
          body: { ...payload },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (data.status === "success") {
            if (arg.isTrending) {
              toast.success("Event Set as Trending");
            } else {
              toast.success("Event Removed from Trending");
            }
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),
    adminUpdateEventFlag: builder.mutation<BaseApiResponse, UpdateEventFlag>({
      query: ({ id, ...payload }) => {
        return {
          url: `admin/events/${id}/flagged-status`,
          method: "PATCH",
          body: { ...payload },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (data.status === "success") {
            if (arg.isFlagged) {
              toast.success("Event Flagged!");
            } else {
              toast.success("Event flag removed!");
            }
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),
    getRecentEvents: builder.query<RecentEventsResponse, void>({
      query: () => {
        return {
          url: `admin/top-recent-events`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getAdminDashboardStats: builder.query<
      AdminDashboardStats,
      AdminOverviewQueryParams
    >({
      query: (params) => {
        return {
          url: `admin/platform-stats`,
          method: "GET",
          params: { ...params },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getMonthlyEventSummary: builder.query<
      MonthlyEventsSummary,
      FinancialSummaryParams
    >({
      query: ({ month, year, currency, ...params }) => {
        return {
          url: `admin/event-summary`,
          method: "GET",
          params: {
            month,
            year,
            currency,
            ...params,
          },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getMonthlyEventGuestList: builder.query<
      MonthlyEventGuestList,
      FinancialSummaryParams
    >({
      query: ({ month, year, currency, ...params }) => {
        return {
          url: `admin/guest-list`,
          method: "GET",
          params: {
            month,
            year,
            currency,
            ...params,
          },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
  }),
});

export const {
  useGetAllEventsQuery,
  useGetAllUserEventsQuery,
  useAdminGetEventTicketsQuery,
  useAdminGetEventTicketsBreakdownQuery,
  useAdminGetEventGuestQuery,
  useAdminGetEventGuestsQuery,
  useAdminUpdateEventTrendingMutation,
  useAdminUpdateEventFlagMutation,
  useGetRecentEventsQuery,
  useGetAdminDashboardStatsQuery,
  useGetMonthlyEventSummaryQuery,
  useGetMonthlyEventGuestListQuery,
} = adminEventsApi;
